import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';
import {
  Button, Card, Container, Dropdown, Table, Confirm, Checkbox, Popup,
  Dimmer, Form, Grid, Label, Modal, Header, Loader, Message, Segment, Tab, Icon,
} from 'semantic-ui-react'
import * as QUESTIONAIRE_ACTIONS from '../../../actions/questionaireActions';
import * as TENANT_ACTIONS from '../../../actions/tenantActions';
import { HexColorPicker } from 'react-colorful';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useForm, Controller } from 'react-hook-form';
import { buttonSymbolOptions } from './mobileManagmentButtonSymbols';

function MobileManagementPage(props) {


  const DEFAULT_COLOURS = {
    primary: '#3a95a4',
    onPrimary: '#ffffff',
    secondary: '#1e97d5',
    onSecondary: '#ffffff',
    tertiary: '#ecc910'
  };

  const DEFAULT_BUTTON_MAPPING = [];

  const makeColourList = mConfig => ({
    primary: mConfig.colourList?.primary ?? DEFAULT_COLOURS.primary,
    onPrimary: mConfig.colourList?.onPrimary ?? DEFAULT_COLOURS.onPrimary,
    secondary: mConfig.colourList?.secondary ?? DEFAULT_COLOURS.secondary,
    onSecondary: mConfig.colourList?.onSecondary ?? DEFAULT_COLOURS.onSecondary,
    tertiary: mConfig.colourList?.tertiary ?? DEFAULT_COLOURS.tertiary
  });

  const [state, setState] = useState(() => {
    const mConfig = props.mobileConfig || {};
    return {
      mobileConfig: { ...mConfig },

      fetchingData: false,

      colourList: makeColourList(mConfig),
      buttonMapping: Array.isArray(mConfig.buttonMapping) && mConfig.buttonMapping.length
        ? mConfig.buttonMapping.map(btn => ({
          ...btn,
          questionaire: Array.isArray(btn.questionaire) ? btn.questionaire[0]?.qNaireId || null : btn.questionaire,
          offlineQuestionnaire: btn.offlineQuestionnaire ?? false
        }))
        : DEFAULT_BUTTON_MAPPING,
      nextButtonId: mConfig.buttonMapping?.length ? Math.max(...mConfig.buttonMapping.map(b => b.id)) + 1 : 1,

      canEdit: false,
      editInput: false,
      editButtonSymbol: false,
      activeButtonId: null,
      editColourOrButtonName: '',
      showConfirmDelete: false,
      deleteButtonCandidate: null,
    };
  });

  const colourGroups = [
    { colors: ['primary', 'onPrimary'], title: 'Primary Colors' },
    { colors: ['secondary', 'onSecondary'], title: 'Secondary Colors' },
    { colors: ['tertiary'], title: 'Tertiary Color' }
  ];






  const {
    handleSubmit,
    reset,
    formState: { errors },
    control
  } = useForm({
    mode: 'all',
    reValidateMode: 'onBlur'
  });


  const hasPermissions = (category) => {
    return (
      !_.isEmpty(props.userPermissions) &&
      props.userPermissions.hasOwnProperty(category)
    );
  };

  const isAdmin = () => {
    return (
      hasPermissions("Other") && props.userPermissions["Other"].actions.admin
    );
  };

  const canReadMobileManagement = () => {
    return hasPermissions('Mobile Management') && props.userPermissions['Mobile Management'].actions.READ
      || isAdmin();
  }

  const canUpdateMobileManagement = () => {
    return (hasPermissions('Mobile Management') && props.userPermissions['Mobile Management'].actions.UPDATE && canReadMobileManagement())
      || isAdmin();
  }


  useEffect(() => {
    const mConfig = props.mobileConfig || {};
    setState(prevState => ({
      ...prevState,
      mobileConfig: { ...mConfig },

      colourList: makeColourList(mConfig),

      buttonMapping: Array.isArray(mConfig.buttonMapping) && mConfig.buttonMapping.length
        ? mConfig.buttonMapping
        : DEFAULT_BUTTON_MAPPING,
      nextButtonId: mConfig.buttonMapping?.length ? Math.max(...mConfig.buttonMapping.map(b => b.id)) + 1 : 1,
      
      canEdit: false,
      editInput: false,
      editButtonSymbol: false,
      activeButtonId: null,
    }));

  }, [props.mobileConfig]);

  useEffect(() => {
    if (!_.isEmpty(props.tenant)) {
      if (props.isPermissionsFetched) {
        if (canReadMobileManagement()) {
          props.fetchQuestionairesAction(props.tenant.id);

        } else {
          toast.error('You do not have permission to manage the mobile application');
        }
      }
    }
  }, [props.tenant]);


  useEffect(() => {
    const btn = state.buttonMapping.find(b => b.id === state.activeButtonId);
  }, [state.buttonMapping, state.activeButtonId]);

  const questionaireOptions = [
    ...props.questionaires
      .map((questionaire, idx) => ({
        id: idx + 1,
        key: questionaire.id,
        text: questionaire.name,
        value: questionaire.id,
      }))
      .sort((a, b) => a.text.localeCompare(b.text)),
  ];

  const originalColours = React.useMemo(
    () => makeColourList(props.mobileConfig || {}),
    [props.mobileConfig]
  );

  const originalMapping = React.useMemo(
    () => (
      Array.isArray(props.mobileConfig?.buttonMapping) && props.mobileConfig.buttonMapping.length
        ? props.mobileConfig.buttonMapping
        : DEFAULT_BUTTON_MAPPING
    ),
    [props.mobileConfig]
  );

  const userEdited =
    !_.isEqual(state.colourList, originalColours) ||
    !_.isEqual(state.buttonMapping, originalMapping);


  const onSubmit = () => {
    let payload = {
      ...props.tenant,
      mobileConfig: {
        buttonMapping: state.buttonMapping,
        colourList: state.colourList
      }
    }
    props.saveTenantAction(payload)
  }



  const openNameModal = (button) => {
    setState(prevState => ({
      ...prevState,
      editInput: true,
      editColourOrButtonName: 'buttonName',
      activeButtonId: button.id,
    }));
    reset({ input_value: button.name });
  };

  const openColourModal = (colorKey) => {
    const currentHex = state.colourList[colorKey];

    setState(prevState => ({
      ...prevState,
      editColourOrButtonName: colorKey,
      editInput: true,
    }));
    reset({ input_value: currentHex });
  };

  const openSymbolModal = (button) => {
    setState(prevState => ({
      ...prevState,
      editButtonSymbol: true,
      activeButtonId: button.id,
    }));
    reset({ input_value: button.symbol });
  };

  const addButton = () => {
    const newButton = {
      id: state.nextButtonId,
      symbol: 'fa-solid fa-plus',
      name: `Button ${state.nextButtonId}`,
      questionaire: null,
      offlineQuestionnaire: false
    };
    setState(prev => ({
      ...prev,
      buttonMapping: [...prev.buttonMapping, newButton],
      nextButtonId: prev.nextButtonId + 1
    }));
  };

  const removeButton = (button) => {
    setState(prev => ({
      ...prev,
      showConfirmDelete: true,
      deleteButtonCandidate: button
    }));
  };

  const confirmDeleteButton = () => {
    setState(prev => ({
      ...prev,
      buttonMapping: prev.buttonMapping.filter(btn => btn.id !== prev.deleteButtonCandidate.id),
      showConfirmDelete: false,
      deleteButtonCandidate: null
    }));
  };

  const onQuestionnaireChange = (buttonId, qNaireId) => {
    setState(prev => ({
      ...prev,
      buttonMapping: prev.buttonMapping.map(btn =>
        btn.id === buttonId ? { ...btn, questionaire: qNaireId } : btn
      )
    }));
  };

  const onOfflineToggle = (buttonId, checked) => {
    setState(prev => ({
      ...prev,
      buttonMapping: prev.buttonMapping.map(btn =>
        btn.id === buttonId ? { ...btn, offlineQuestionnaire: checked } : btn
      )
    }));
  };

  const onEditSymbolSubmit = (data) => {
    setState(state => ({
      ...state,
      buttonMapping: state.buttonMapping.map(btn =>
        btn.id === state.activeButtonId
          ? { ...btn, symbol: data.input_value }
          : btn
      ),
      editButtonSymbol: false,
    }));
  };

  const EditButtonSymbolModal = () => {
    if (!state.editButtonSymbol) return null;
    return (
      <Modal size="small" open={state.editButtonSymbol} onClose={() => setState((prevState) => ({ ...prevState, editButtonSymbol: false }))}>
        <Modal.Header>Select Symbol</Modal.Header>
        <Modal.Content>
          <Form onSubmit={handleSubmit(onEditSymbolSubmit)} size="small">
            <Form.Field error={!!errors.input_value}>
              <Controller
                name="input_value"
                control={control}
                defaultValue=""
                rules={{ required: true }}
                render={({ field }) => (
                  <Grid columns={5} padded>
                    {buttonSymbolOptions.map((iconClass) => (
                      <Grid.Column key={iconClass} textAlign="center">
                        <Button
                          basic
                          circular
                          type='button'
                          active={field.value === iconClass}
                          onClick={() => field.onChange(iconClass)}
                          style={{ backgroundColor: state.colourList.primary }}
                        >
                          <FontAwesomeIcon
                            icon={iconClass}
                            size="2x"
                            style={{
                              color: field.value === iconClass ? state.colourList.onPrimary : undefined
                            }}
                          />
                        </Button>
                      </Grid.Column>
                    ))}
                  </Grid>
                )}
              />
              {errors.input_value && <p>* Please choose one</p>}
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button className="danger" size="small" onClick={() => setState((prevState) => ({ ...prevState, editButtonSymbol: false }))}>
            Cancel
          </Button>
          <Button className="success" size="small" type="submit" positive disabled={!!errors.input_value} onClick={handleSubmit(onEditSymbolSubmit)}>
            Ok
          </Button>
        </Modal.Actions>
      </Modal>
    )
  }

  const EditButtonNameModal = () => {
    if (!state.editInput) return null;

    const field = state.editColourOrButtonName;
    const isName = field === 'buttonName';
    const isColour = ['primary', 'onPrimary', 'secondary', 'onSecondary', 'tertiary'].includes(field);

    const labelText = isName
      ? 'Edit Button Name'
      : `Edit ${field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1')} Color`;

    const rules = isName
      ? { required: true }
      : {
        required: true,
        pattern: {
          value: /^#[0-9A-Fa-f]{6}$/,
          message: 'Must be a hex code like #A1B2C3',
        },
      };

    const onSubmit = (data) => {
      if (isName) {
        setState(prevState => ({
          ...prevState,
          buttonMapping: prevState.buttonMapping.map(b =>
            b.id === prevState.activeButtonId
              ? { ...b, name: data.input_value }
              : b
          ),
          editInput: false,
        }));
      } else if (isColour) {
        setState(prevState => ({
          ...prevState,
          colourList: {
            ...prevState.colourList,
            [field]: data.input_value
          },
          editInput: false
        }));
      }
    };

    return (
      <Modal size="small" open={state.editInput} onClose={() => setState(prevState => ({ ...prevState, editInput: false }))}>
        <Modal.Header>{labelText}</Modal.Header>
        <Modal.Content>
          <Form size="small" onSubmit={handleSubmit(onSubmit)}>
            <Form.Field error={!!errors.input_value}>
              <Controller
                name="input_value"
                control={control}
                defaultValue=""
                rules={rules}
                render={({ field }) => (
                  <input
                    {...field}
                    autoFocus
                    placeholder={isName ? 'Enter name' : '#ffffff'}
                    className={errors.input_value ? 'invalid-input-field' : ''}
                  />
                )}
              />
              {errors.input_value?.message && (
                <p className="validation-error">
                  {errors.input_value.message}
                </p>
              )}
              {errors.input_value?.type === 'required' && isName && (
                <p className="validation-error">*A button name is required</p>
              )}
              {errors.input_value?.type === 'maxLength' && isName && (
                <p className="validation-error">* Max 20 characters</p>
              )}
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button size="mini" onClick={() => setState(s => ({ ...s, editInput: false }))}>
            Cancel
          </Button>
          <Button size="mini" positive type="submit" onClick={handleSubmit(onSubmit)} disabled={!!errors.input_value}>
            Ok
          </Button>
        </Modal.Actions>
      </Modal>
    );
  };


  const renderColourGroup = ({ colors, title }) => {
    return (
      <Segment key={title}>
        <Header as="h3" dividing>
          {title}
        </Header>
        <Grid columns={colors.length} stackable>
          {colors.map(colorKey => {
            const val = state.colourList[colorKey];
            const displayName = colorKey.charAt(0).toUpperCase() + colorKey.slice(1).replace(/([A-Z])/g, ' $1');

            return (
              <Grid.Column key={colorKey}>
                <Segment>
                  <Header as="h4">{displayName}</Header>
                  <Grid>
                    <Grid.Row>
                      <Grid.Column width={4}>
                        <div
                          style={{
                            width: '40px',
                            height: '40px',
                            backgroundColor: val,
                            borderRadius: '4px',
                            border: '1px solid #ccc'
                          }}
                        />
                      </Grid.Column>
                      <Grid.Column width={12}>
                        <Button
                          disabled={!state.canEdit}
                          fluid
                          basic
                          onClick={() => state.canEdit && openColourModal(colorKey)}
                        >
                          {val}
                        </Button>
                      </Grid.Column>
                    </Grid.Row>
                  </Grid>
                </Segment>
              </Grid.Column>
            );
          })}
        </Grid>
      </Segment>
    );
  };

  const moveButton = (fromIndex, toIndex) => {
    setState(prev => {
      const newMapping = [...prev.buttonMapping];
      const [moved] = newMapping.splice(fromIndex, 1);
      newMapping.splice(toIndex, 0, moved);
      return { ...prev, buttonMapping: newMapping };
    });
  };

  const buttonList = () => {
    const editable = state.canEdit && canUpdateMobileManagement();

    return (
      <div style={{ overflow: 'visible', position: 'relative', zIndex: 1000 }}>
        <Grid>
          <Grid.Row>
            <Grid.Column width='12'>
              <h3>Mobile App Buttons</h3>
            </Grid.Column>
            <Grid.Column width='4'>
              {editable && (
                <Button compact className="success" icon='add' size='mini' floated='right' onClick={addButton} />
              )}
            </Grid.Column>
          </Grid.Row>
        </Grid>

        <Grid columns={2} stackable>
          {state.buttonMapping.map((button, index) => (
            <Grid.Column key={button.id}>
              <Segment>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                  {editable && (
                    <div style={{ display: 'flex', flexDirection: 'column', marginRight: '0.5rem' }}>
                      <Button
                        icon='chevron up'
                        size='mini'
                        basic
                        disabled={index === 0}
                        onClick={() => moveButton(index, index - 1)}
                      />
                      <Button
                        icon='chevron down'
                        size='mini'
                        basic
                        disabled={index === state.buttonMapping.length - 1}
                        onClick={() => moveButton(index, index + 1)}
                      />
                    </div>
                  )}
                  <div
                    style={{
                      width: '60px',
                      height: '60px',
                      borderRadius: '12px',
                      background: state.colourList.primary,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: editable ? 'pointer' : 'default',
                      border: editable ? '2px dashed #ccc' : 'none',
                      transition: 'all 0.2s ease',
                      marginRight: '1rem'
                    }}
                    onClick={() => editable && openSymbolModal(button)}
                  >
                    <FontAwesomeIcon
                      icon={button.symbol || 'fa-solid fa-gear'}
                      size="lg"
                      color="white"
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <h4
                      style={{
                        margin: 0,
                        cursor: editable ? 'pointer' : 'default',
                        textDecoration: editable ? 'underline' : 'none',
                        color: editable ? '#1e70bf' : 'inherit'
                      }}
                      onClick={() => editable && openNameModal(button)}
                    >
                      {button.name}
                    </h4>
                  </div>
                  {editable && (
                    <Button compact className='danger' icon='trash' size='mini' onClick={() => removeButton(button)} />
                  )}
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Questionnaire</label>
                  <Dropdown
                    fluid
                    selection
                    search
                    scrolling
                    selectOnNavigation={false}
                    placeholder="Select questionnaire"
                    options={questionaireOptions}
                    value={button.questionaire || ''}
                    disabled={!editable}
                    onChange={(e, { value }) => onQuestionnaireChange(button.id, value)}
                    style={{ maxHeight: '300px' }}
                  />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <label style={{ fontWeight: 'bold' }}>Offline Questionnaire</label>
                  <Popup
                    content="If enabled, the questionnaire will need to be downloaded to be used offline"
                    trigger={
                      <Checkbox
                        toggle
                        checked={button.offlineQuestionnaire || false}
                        disabled={!editable || !button.questionaire}
                        onChange={(e, { checked }) => onOfflineToggle(button.id, checked)}
                      />
                    }
                  />
                </div>
              </Segment>
            </Grid.Column>
          ))}
        </Grid>
      </div>
    );
  };

  if (props.fetchingData) {
    return (
      <div style={{ height: "200px", margin: "10px 0px" }}>
        <Dimmer inverted active>
          <Loader content='Loading' />
        </Dimmer>
      </div>
    );
  } else if (canReadMobileManagement()) {
    return (
      <Container fluid>
        <Grid>
          <Grid.Row>
            <Grid.Column width='12'>
              <h3>Mobile Management</h3>
            </Grid.Column>
            <Grid.Column width='4'>
              {canUpdateMobileManagement() && (
                !state.canEdit ? (
                  <Button compact className='warning' icon='edit' size='mini' floated='right' onClick={() => setState(s => ({ ...s, canEdit: true }))} />
                ) : (
                  <div>
                    <Button compact className='danger' size='mini' floated='right' onClick={() => setState((prevState) => ({ ...prevState, canEdit: false }))}>
                      Cancel
                    </Button>
                    <Button compact className='success' size='mini' floated='right' onClick={onSubmit}>
                      Save
                    </Button>
                  </div>
                )
              )}
            </Grid.Column>
          </Grid.Row>
        </Grid>



        <Segment>
          {buttonList()}
          <br />
          {colourGroups.map(cGroup => renderColourGroup(cGroup))}
        </Segment>

        <Confirm
          content={`Are you sure you want to delete button: ${state.deleteButtonCandidate?.name}?`}
          open={state.showConfirmDelete}
          onCancel={() => setState(s => ({ ...s, showConfirmDelete: false, deleteButtonCandidate: null }))}
          onConfirm={confirmDeleteButton}
        />


        <EditButtonSymbolModal />
        <EditButtonNameModal />
      </Container>
    );
  } else {
    return (
      <Message negative content="You do not have permission to view this page." />
    )
  }
}

const mapStateToProps = (state, ownProps) => {
  return {
    userPermissions: _.isEmpty(state.authUserRoles.permissions) ? {} : state.authUserRoles.permissions,
    isPermissionsFetched: state.isPermissionsFetched === true,
    fetchingData: state.fetchingData === true,
    tenant: state.tenantManagement.activeTenant,
    mobileConfig: state.tenantManagement.activeTenant.mobileConfig,
    questionaires: _.isEmpty(state.questionaires) ? [] : Object.values(state.questionaires),

  };
}

export default connect(mapStateToProps,
  (dispatch, ownProps) => ({
    fetchQuestionairesAction: (payload) => dispatch(QUESTIONAIRE_ACTIONS.fetchQuestionairesAction(payload)),
    saveTenantAction: (tenant) => dispatch(TENANT_ACTIONS.saveTenantAction(tenant)),
  })
)(MobileManagementPage);
